import * as constants from './constants';
import {
  type FileExtensionError,
  FileExtensionErrorReason,
  getFileExtensionErrorMessage,
  parseFileExtension,
  sanitizeFileExtension,
} from './extensions';
import { isMimeType } from './mimeTypes';

export enum FileErrorReason {
  InvalidExtension = 'InvalidExtension',
  InvalidMimeType = 'InvalidMimeType',
  MaliciousFile = 'MaliciousFile',
  MissingExtension = 'MissingExtension',
  TooLarge = 'TooLarge',
}

type FileErrorConfig<R extends FileErrorReason = FileErrorReason> = Extract<
  | {
      readonly extension: string;
      readonly mimeType?: never;
      readonly reason: FileErrorReason.InvalidExtension;
    }
  | {
      readonly extension?: never;
      readonly mimeType: string;
      readonly reason: FileErrorReason.InvalidMimeType;
    }
  | {
      readonly extension?: never;
      readonly mimeType?: never;
      readonly reason: FileErrorReason.MaliciousFile;
    }
  | {
      readonly extension?: never;
      readonly mimeType?: never;
      readonly reason: FileErrorReason.MissingExtension;
    }
  | {
      readonly extension?: never;
      readonly mimeType?: never;
      readonly reason: FileErrorReason.TooLarge;
    },
  { reason: R }
>;

const FileErrorMessages: {
  [key in FileErrorReason]: (config: FileErrorConfig<key>) => string;
} = {
  [FileErrorReason.InvalidExtension]: config =>
    getFileExtensionErrorMessage({
      extension: config.extension,
      reason: FileExtensionErrorReason.InvalidExtension,
    }),
  [FileErrorReason.InvalidMimeType]: config => `The file type '${config.mimeType}' is invalid!`,
  [FileErrorReason.MaliciousFile]: () =>
    'Known malicious file detected. Please upload a different file.',
  [FileErrorReason.MissingExtension]: () =>
    getFileExtensionErrorMessage({ reason: FileExtensionErrorReason.MissingExtension }),
  [FileErrorReason.TooLarge]: () => 'File exceeds the 10MB size limit. Reduce size and try again.',
};

export const getFileErrorMessage = <R extends FileErrorReason>(
  config: FileErrorConfig<R>,
): string => FileErrorMessages[config.reason](config);

/**
 * An error, {@link Error}, that is used to represent cases where a file, {@link File}, is
 * considered to be invalid.
 */
export class FileError<R extends FileErrorReason = FileErrorReason> extends Error {
  public readonly extension: FileErrorConfig<R>['extension'];
  public readonly mimeType: FileErrorConfig<R>['mimeType'];
  public readonly reason: R;

  private constructor(config: FileErrorConfig<R>) {
    super(getFileErrorMessage(config));
    this.name = 'FileExtensionError';
    this.reason = config.reason;
    this.mimeType = config.mimeType as FileErrorConfig<R>['mimeType'];
    this.extension = config.extension as FileErrorConfig<R>['extension'];
  }

  public static fromFileExtensionError(
    error: FileExtensionError,
  ): FileError<FileErrorReason.InvalidExtension> | FileError<FileErrorReason.MissingExtension> {
    switch (error.reason) {
      case FileExtensionErrorReason.InvalidExtension:
        return FileError.InvalidExtension(error.extension);
      case FileExtensionErrorReason.MissingExtension:
        return FileError.MissingExtension();
    }
  }

  public static InvalidExtension(extension: string): FileError<FileErrorReason.InvalidExtension> {
    return new FileError({ extension, reason: FileErrorReason.InvalidExtension });
  }

  public static InvalidMimeType(mimeType: string): FileError<FileErrorReason.InvalidMimeType> {
    return new FileError({ mimeType, reason: FileErrorReason.InvalidMimeType });
  }

  public static MaliciousFile(): FileError<FileErrorReason.MaliciousFile> {
    return new FileError({ reason: FileErrorReason.MaliciousFile });
  }

  public static MissingExtension(): FileError<FileErrorReason.MissingExtension> {
    return new FileError({ reason: FileErrorReason.MissingExtension });
  }

  public static TooLarge(): FileError<FileErrorReason.TooLarge> {
    return new FileError({ reason: FileErrorReason.TooLarge });
  }
}

type NonStrictValidatedFile =
  | { readonly error: FileError; readonly sanitizedFile?: never }
  | { readonly error?: never; readonly sanitizedFile: File };

/**
 * Represents the return of the {@link validateFile} method that is conditional on the options,
 * {@link ValidateFileOptions}, that are provided to the method.
 */
type ValidatedFile<O extends ValidateFileOptions> = O extends { readonly strict: false }
  ? NonStrictValidatedFile
  : File;

interface ValidateFileOptions {
  /**
   * Determines whether or not an error, {@link FileError}, should be thrown if the file is
   * deemed to be invalid.  If the 'strict' option is 'false', the method will instead return an
   * object, {@link NonStrictValidatedFile}, that contains a descriptive {@link FileError} object
   * if the file is deemed to be invalid.
   *
   * Default: true
   */
  readonly strict?: boolean;
}

const handleValidationError = <O extends ValidateFileOptions>(
  error: { [K in FileErrorReason]: FileError<K> }[FileErrorReason],
  options?: O,
): ValidatedFile<O> => {
  if (options?.strict === false) {
    return { error } as ValidatedFile<O>;
  }
  throw error;
};

/**
 * Validates the file, {@link File}, based on it's extension, size, name and type.  The manner in
 * which the method returns or throws depends on the optional options that can be provided to the
 * method, {@link ValidateFileOptions}.
 *
 * @param {File} file
 *   The file, {@link File}, for which the validation is being performed.
 *
 *   @see {ValidatedFile}
 *
 * @example
 * const { error } = validateFile(file, { strict: false });
 * if (error) {
 *   console.log(`Encountered an Invalid File: ${error.message}!`)
 * }
 * @example
 * // Will throw an error if invalid.
 * validateFile(file);
 *
 * @throws {FileError}
 *   An error, {@link FileError}, if the file is deemed to be invalid and the 'strict' option is not
 *   explicitly false.
 *
 * @returns {ValidatedFile<O>}
 *   Either the original file, {@link File}, that is provided to the method - in the case that the
 *   'strict' option is not explicitly 'false' - or an object that contains an optional 'error'
 *   property - in the case that the 'strict' option is explicitly false.
 */
export const validateFile = <O extends ValidateFileOptions>(
  file: File,
  options?: O,
): ValidatedFile<O> => {
  if (file.size > constants.MaxFileSize) {
    return handleValidationError(FileError.TooLarge(), options);
  } else if (constants.InvalidFilenames.includes(file.name.trim())) {
    return handleValidationError(FileError.MaliciousFile(), options);
  }

  const { error: sanitizeError, sanitizedFile } = sanitizeFileExtension(file, { strict: false });

  if (sanitizeError) {
    const error = FileError.fromFileExtensionError(sanitizeError);
    if (options?.strict === false) {
      return { error } as ValidatedFile<O>;
    }
    throw error;
  }

  const { error: extensionError } = parseFileExtension(sanitizedFile, { strict: false });

  if (extensionError) {
    const error = FileError.fromFileExtensionError(extensionError);
    if (options?.strict === false) {
      return { error } as ValidatedFile<O>;
    }
    throw error;
  } else if (!isMimeType(sanitizedFile.type)) {
    return handleValidationError(FileError.InvalidMimeType(sanitizedFile.type), options);
  }

  if (options?.strict === false) {
    return { sanitizedFile } as ValidatedFile<O>;
  }
  return sanitizedFile as ValidatedFile<O>;
};
